import React from 'react'
import { Alert, Divider, Stack, Typography } from '@mui/material'
import DocLink from '../../components/DocLink'

const PeopleShifts = () => (
  <Stack gap={3} divider={<Divider />}>
    <Stack gap={1}>
      <Typography variant="title3">Who can use these tools</Typography>
      <Typography>
        The People section (employee records, subcontractors, applications,
        training, and DBS checks) requires the Personnel team; site
        administrators always have access. Shift pages are available more
        widely: every staff member gets the My Work tools, while each
        Workforce entry has its own rule — Shift Applications is shown to
        Personnel and Event Planning, Global Roster to Personnel, Staff
        Availability to Personnel and Manager, and Overtime Claims and
        Payroll to Personnel (on Premium and above). PTS Co-Ordinator access
        on its own unlocks none of the Workforce tools.
      </Typography>
      <Alert severity="info" variant="outlined">
        The sidebar is permission-aware. Frontline staff normally see My Work:
        Available Shifts, My Shifts, My Availability, and My Pay. Personnel
        users see the full Workforce section plus employee records and
        compliance tools.
      </Alert>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Workforce dashboard and records</Typography>
      <Typography>
        The workforce dashboard combines headcount and compliance charts with
        an Action Centre listing everything that needs attention — expired
        training, DBS and driving licence issues, missing ID cards, training
        uploads to approve, and appraisals awaiting review. See{' '}
        <DocLink
          docsPage
          url="/employees-hr/personnel-dashboard"
          text="Workforce Dashboard"
        />{' '}
        for the full guide. Employee records hold personal details, contact
        information, addresses, professional qualifications, controlled drugs
        information, next of kin, driving licence details, documents, notes,
        training, appraisals, compliance status, and account access.
        Subcontractors are managed separately from{' '}
        <DocLink url="/employees/subcontractors" text="Subcontractors" />
        — see the{' '}
        <DocLink
          docsPage
          url="/employees-hr/subcontractors"
          text="Subcontractors"
        />
        guide.
      </Typography>
      <Typography>
        Use <DocLink url="/employeesdashboard" text="Workforce Dashboard" />
        <DocLink url="/employees" text="Employees" />
        and <DocLink url="/employees/new" text="New Employee" /> to manage
        records.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Applications and onboarding</Typography>
      <Typography>
        Employee applications are handled separately from active employee
        records. Managers can review submitted applications, request references,
        complete checks, and convert suitable applicants into employee records.
      </Typography>
      <Typography>
        Applications are a tier 2 feature and are available from{' '}
        <DocLink url="/employees/applications" text="Applications" />. See{' '}
        <DocLink
          docsPage
          url="/employees-hr/recruitment-applications"
          text="Recruitment & Applications"
        />{' '}
        for where to send people who want to apply and the full review process.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Rosters, availability, and leave</Typography>
      <Typography>
        Global Roster lets managers create named draft rota plans, create or
        repeat roster shifts, assign staff, publish assigned work, archive
        unused draft rosters, and review shift applications in context. Event
        and PTS shifts appear in the roster and can be edited directly from
        it, still link back to their source modules for full context, and can
        be copied into the current draft rota plan as planning templates.
        Staff submit annual leave, sickness, and unavailable periods from My
        Availability, while managers review requests from Staff Availability.
        Approved leave is included in assignment checks before shifts are saved.
      </Typography>
      <Typography>
        A rota plan can also be marked as an on-call rota, restricted to the
        Manager and Supervisor teams. Whoever is currently on call shows on
        the Home Screen with a tap-to-call action — see{' '}
        <DocLink
          docsPage
          url="/employees-hr/rostering-payroll"
          text="Rostering, Availability, and Payroll"
        />{' '}
        for details.
      </Typography>
      <Typography>
        My Availability also shows staff their holiday balance, including
        accrued leave, approved annual leave, pending requests, and remaining
        entitlement. Open <DocLink url="/global-roster" text="Global Roster" />
        <DocLink url="/my-availability" text="My Availability" />
        and <DocLink url="/employees/availability" text="Staff Availability" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Training, DBS, pay runs, and overtime</Typography>
      <Typography>
        Mandatory training can be reviewed by managers and completed by staff
        from their own training page. DBS checks have a dedicated dashboard.
        Staff appraisals run through{' '}
        <DocLink url="/my-appraisals" text="My Appraisals" /> and an HR review
        queue — see{' '}
        <DocLink docsPage url="/employees-hr/appraisals" text="Appraisals" />.
        Staff also confirm or update their occupational health screening as
        the first step of each appraisal, or at any time from{' '}
        <DocLink url="/my-occupational-health" text="My Occupational Health" />{' '}
        — see{' '}
        <DocLink
          docsPage
          url="/employees-hr/occupational-health"
          text="Occupational Health"
        />
        .
        Payroll now uses pay runs: managers group verified unpaid shifts, review
        PAYE and invoice totals, then mark a draft run as paid. Approved
        overtime is included in pay-run totals; pending or rejected overtime is
        not included, and a decided claim can be undone back to Pending until
        the shift is paid or in a pay run. Invoice workers can upload staff pay invoices against
        completed unpaid shifts, and personnel users can review or upload those
        invoices from Payroll. Employee payroll profiles, including contracted
        hours and annual leave settings, are managed from the employee record
        Payroll tab.
      </Typography>
      <Typography>
        Key pages include{' '}
        <DocLink
          url="/employees/mandatory-training"
          text="Mandatory Training"
        />
        <DocLink url="/mandatory-training" text="My Training" />
        <DocLink url="/dbs-checks" text="DBS Checks" />
        <DocLink url="/overtime-claims" text="Overtime Claims" />
        <DocLink url="/employees/payroll" text="Payroll" />
        and <DocLink url="/my-pay" text="My Pay" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Shift booking</Typography>
      <Typography>
        Staff use Available Shifts to apply for work and My Shifts to review
        their confirmed or upcoming shifts. Several identical vacancies —
        four Paramedics on the same event day, for example — show as a
        single listing rather than one row each, and managers can allocate
        or reject a whole vacancy's applicants at once from Shift
        Applications. Published roster assignments can be acknowledged from
        My Shifts. If a staff member marks that they cannot attend, the
        shift remains assigned to them, is offered to colleagues as a swap,
        and managers are notified so they can arrange cover. Staff who can no longer work a
        confirmed shift can offer it to colleagues as a swap — see{' '}
        <DocLink docsPage url="/employees-hr/shift-swaps" text="Shift Swaps" />.
      </Typography>
      <Typography>
        My Pay separates paid statements from Pending Pay. Pending Pay shows
        estimated pay for completed unpaid shifts and indicates whether each
        shift is awaiting verification, ready for payroll, or already included
        in a draft pay run. Invoice workers can also upload and track staff pay
        invoices linked to those completed unpaid shifts.
      </Typography>
      <Typography>
        Open <DocLink url="/available-shifts" text="Available Shifts" />
        <DocLink url="/my-shifts" text="My Shifts" />
        <DocLink url="/shift-applications" text="Shift Applications" />
        and <DocLink url="/global-roster" text="Global Roster" />.
      </Typography>
    </Stack>

    <Stack gap={1}>
      <Typography variant="title3">Workflow guide</Typography>
      <Typography>
        For a step-by-step guide covering roster planning, shift publication,
        acknowledgement, leave balances, overtime, pay runs, and staff pay
        summaries, open{' '}
        <DocLink
          docsPage
          url="/employees-hr/workforce-scheduling-payroll"
          text="Rostering, Pay Runs, and Leave"
        />.
      </Typography>
    </Stack>
  </Stack>
)

export default PeopleShifts
